import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import toast, { Toaster } from 'react-hot-toast';
import { errorCoordinator } from '@/services/errorCoordinator';

// Loading Components
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageLoader } from '@/shared/components/PageLoader';

// Layouts (not lazy-loaded - needed immediately)
import MainLayout from '@/shared/components/layout/MainLayout';

// Lazy-loaded Pages
const ThemedLoginRouter = lazy(() => import('@/features/auth/pages/ThemedLoginRouter'));
const BasicLogin = lazy(() => import('@/features/auth/pages/BasicLoginPage'));
const GoogleCallbackPage = lazy(() => import('@/features/auth/pages/GoogleCallbackPage'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Users = lazy(() => import('./pages/users/Users'));
const Resources = lazy(() => import('./pages/resources/Resources'));
const Settings = lazy(() => import('@/features/settings/pages/Settings'));

// Lazy-loaded Settings Pages
const SettingsLayout = lazy(() => import('@/features/settings/pages/SettingsLayout'));
const ProfileSettings = lazy(() => import('@/features/settings/pages/ProfileSettings'));
const PluginsSettings = lazy(() => import('@/features/settings/pages/PluginsSettings'));

// Lazy-loaded About Page
const AboutPage = lazy(() => import('./pages/about/AboutPage'));

// Lazy-loaded Booking Pages
const RoomsPage = lazy(() => import('@/features/bookings/pages/RoomsPage'));
const MyBookingsPage = lazy(() => import('@/features/bookings/pages/MyBookingsPage'));
const EquipmentBookingPage = lazy(() => import('@/features/bookings/pages/EquipmentBookingPage'));

// Lazy-loaded Calendar Pages
const CalendarLayout = lazy(() => import('@/features/calendar/pages/CalendarLayout'));
// Legacy redirect component for old URLs
const CalendarLegacyRedirect = lazy(() => import('@/features/calendar/pages/CalendarLegacyRedirect'));

// Lazy-loaded Public Pages (no auth)
const PublicBoardPage = lazy(() => import('@/features/calendar/pages/PublicBoardPage'));
const SharedFilePage = lazy(() => import('@/features/files/pages/SharedFilePage'));

// Lazy-loaded Files Page (Forms & Files / Google Drive)
const FilesPage = lazy(() => import('@/features/files/pages/FilesPage'));

// Lazy-loaded Admin Pages
const AdminSidebarLayoutVideogame = lazy(() => import('@/features/admin/components/AdminSidebarLayoutVideogame'));
const PendingApprovalsPage = lazy(() => import('@/features/admin/pages/PendingApprovalsPage'));
const UsersManagementPage = lazy(() => import('@/features/admin/pages/UsersManagementPage'));
const RoomsManagementPage = lazy(() => import('@/features/admin/pages/RoomsManagementPage'));
const EquipmentManagementPage = lazy(() => import('@/features/admin/pages/EquipmentManagementPage'));
const StaffManagementPage = lazy(() => import('@/features/admin/pages/StaffManagementPage'));
const ExpertiseManagementPage = lazy(() => import('@/features/admin/pages/ExpertiseManagementPage'));
const ActivityLogPage = lazy(() => import('@/features/admin/pages/ActivityLogPage'));
const GoogleCalendarStatusPage = lazy(() => import('@/features/admin/pages/GoogleCalendarStatusPage'));
const CalendarSettingsPage = lazy(() => import('@/features/admin/pages/CalendarSettingsPage'));
const AppearanceAdminPage = lazy(() => import('@/features/admin/pages/AppearanceAdminPage'));
const PluginsManagementPage = lazy(() => import('@/features/admin/pages/PluginsManagementPage'));
const AdminDashboardPage = lazy(() => import('@/features/admin/pages/AdminDashboardPage'));
const RecurringRulesSettingsPage = lazy(() => import('@/features/admin/pages/RecurringRulesSettingsPage'));
const OperatingHoursPage = lazy(() => import('@/features/admin/pages/OperatingHoursPage'));
const BookingLimitsPage = lazy(() => import('@/features/admin/pages/BookingLimitsPage'));

// Lazy-loaded Dashboard Pages
const NotificationsPage = lazy(() => import('@/features/notifications/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));

// Components
import { ProtectedPluginRoute } from '@/features/admin/components/ProtectedPluginRoute';
import { AdminRoute } from '@/features/admin/components/AdminRoute';

// Context Providers
import { GoogleOAuthProvider } from '@react-oauth/google';
import { SystemSettingsProvider } from './contexts/SystemSettingsProvider';
import { ThemeProvider } from './contexts/ThemeProvider';
import { LayoutProvider } from './contexts/LayoutContext';
import { PluginProvider } from './contexts/PluginContext';
import { AImryBubbleProvider } from './contexts/AImryBubbleContext';
import { ArcadeTransitionProvider } from './contexts/ArcadeTransitionContext';


// Hooks
import { useAuthStore } from '@/features/auth';

// Support Widget - now integrated in navbar via AImryNavButton

// Config
import { ROUTES, DEFAULT_AUTHENTICATED_ROUTE, DEFAULT_UNAUTHENTICATED_ROUTE } from './config/routes';

// Styles
import './styles/globals.css';

// Initialize i18n
import i18n from './i18n';

// Create Query Client
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      const status = (error as any)?.response?.status;
      if (status === 401) return; // auth interceptor handles 401s
      if (!status || status >= 500) {
        const isServerError = !status || (error as any)?.code === 'ERR_NETWORK';
        const sourceKey = `query:${(error as any)?.config?.url || 'unknown'}`;
        if (isServerError || !navigator.onLine) {
          errorCoordinator.handleServerError(error, sourceKey);
        } else {
          const key = 'errors.serverError';
          toast.error(i18n.t(key, { ns: 'common' }), { id: 'global-query-error' });
        }
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      const status = (error as any)?.response?.status;
      if (status === 401) return;
      if (!status || status >= 500) {
        const isServerError = !status || (error as any)?.code === 'ERR_NETWORK';
        const sourceKey = `mutation:${(error as any)?.config?.url || 'unknown'}`;
        if (isServerError || !navigator.onLine) {
          errorCoordinator.handleServerError(error, sourceKey);
        } else {
          const key = 'errors.serverError';
          toast.error(i18n.t(key, { ns: 'common' }), { id: 'global-mutation-error' });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Return Navigate without wrapping to prevent blank screens
  if (!isAuthenticated) {
    return <Navigate to={DEFAULT_UNAUTHENTICATED_ROUTE} replace />;
  }

  return <>{children}</>;
}

// Public Route Wrapper (redirects to my-bookings if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Return Navigate without wrapping to prevent blank screens
  if (isAuthenticated) {
    return <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />;
  }

  return <>{children}</>;
}

function App() {
  // Log app version, commit hash, and branch on mount
  useEffect(() => {
    const version = '1.0.10'; // From package.json
    const commitHash = import.meta.env.VITE_GIT_COMMIT || 'unknown';
    const branch = import.meta.env.VITE_GIT_BRANCH || 'unknown';

    console.log(`
╔═══════════════════════════════════════════════════════════╗
║           🚀 Chamama Booking App                          ║
╠═══════════════════════════════════════════════════════════╣
║  📦 Version: ${version.padEnd(44)} ║
║  🔖 Commit:  ${commitHash.substring(0, 7).padEnd(44)} ║
║  🌿 Branch:  ${branch.padEnd(44)} ║
╚═══════════════════════════════════════════════════════════╝
    `);
  }, []);

  // Get auth state for routing
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
    <SystemSettingsProvider>
      <ThemeProvider>
        <LayoutProvider>
          <PluginProvider>
            <AImryBubbleProvider>
            <QueryClientProvider client={queryClient}>
            <BrowserRouter>
            <ArcadeTransitionProvider>
              <Routes key={isAuthenticated ? 'authenticated' : 'public'}>
                {/* Public Routes */}
                <Route
                  path={ROUTES.LOGIN}
                  element={
                    <PublicRoute>
                      <Suspense fallback={<LoadingSpinner />}>
                        <ThemedLoginRouter />
                      </Suspense>
                    </PublicRoute>
                  }
                />
                <Route
                  path="/basicLogin"
                  element={
                    <PublicRoute>
                      <Suspense fallback={<LoadingSpinner />}>
                        <BasicLogin />
                      </Suspense>
                    </PublicRoute>
                  }
                />

                {/* Google OAuth Callback (processes redirect from Google) */}
                <Route
                  path={ROUTES.AUTH_CALLBACK}
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <GoogleCallbackPage />
                    </Suspense>
                  }
                />

                {/* Public Board Display (no auth, no layout chrome) */}
                <Route
                  path={ROUTES.BOARD}
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PublicBoardPage />
                    </Suspense>
                  }
                />

                {/* Public Shared File (no auth, redirects to R2 asset) */}
                <Route
                  path={ROUTES.SHARED_FILE}
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SharedFilePage />
                    </Suspense>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path={ROUTES.ROOT}
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />} />
                  <Route
                    path="dashboard"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Dashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="dashboard/notifications"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <NotificationsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Users />
                      </Suspense>
                    }
                  />
                  <Route
                    path="resources"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Resources />
                      </Suspense>
                    }
                  />

                  {/* Booking Routes (Plugin-Protected) */}
                  <Route
                    path="bookings/rooms"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <RoomsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/equipment"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <EquipmentBookingPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/my-bookings"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <MyBookingsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/my-bookings/:filter"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <MyBookingsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/my-bookings/:filter/:bookingId"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <MyBookingsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  {/* Calendar Routes - Unified with query params: /calendar?view=month&type=rooms */}
                  <Route
                    path="calendar"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <CalendarLayout />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  {/* Deep link route for booking modal */}
                  <Route
                    path="calendar/booking/:bookingId"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <CalendarLayout />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  {/* Legacy URL redirects (rooms, equipment paths) */}
                  <Route
                    path="calendar/rooms/*"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CalendarLegacyRedirect type="rooms" />
                      </Suspense>
                    }
                  />
                  <Route
                    path="calendar/equipment/*"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CalendarLegacyRedirect type="equipment" />
                      </Suspense>
                    }
                  />

                  {/* Files Route (Forms & Files - always available, no plugin gate) */}
                  <Route
                    path="files"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <FilesPage />
                      </Suspense>
                    }
                  />

                  {/* About Page */}
                  <Route
                    path="about"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <AboutPage />
                      </Suspense>
                    }
                  />

                  {/* Settings with nested routes */}
                  <Route
                    path="settings"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SettingsLayout />
                      </Suspense>
                    }
                  >
                    <Route index element={<Navigate to={ROUTES.SETTINGS_PROFILE} replace />} />
                    <Route
                      path="profile"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ProfileSettings />
                        </Suspense>
                      }
                    />
                    <Route
                      path="plugins"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PluginsSettings />
                        </Suspense>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Settings />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>

                {/* Admin Routes — dedicated admin layout (outside MainLayout) */}
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <AdminSidebarLayoutVideogame />
                        </Suspense>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="approvals" replace />} />
                  <Route path="approvals" element={<Suspense fallback={<PageLoader />}><PendingApprovalsPage /></Suspense>} />
                  <Route path="approvals/:bookingId" element={<Suspense fallback={<PageLoader />}><PendingApprovalsPage /></Suspense>} />
                  <Route path="users" element={<Suspense fallback={<PageLoader />}><UsersManagementPage /></Suspense>} />
                  <Route path="rooms" element={<Suspense fallback={<PageLoader />}><RoomsManagementPage /></Suspense>} />
                  <Route path="equipment" element={<Suspense fallback={<PageLoader />}><EquipmentManagementPage /></Suspense>} />
                  <Route path="staff" element={<Suspense fallback={<PageLoader />}><StaffManagementPage /></Suspense>} />
                  <Route path="expertises" element={<Suspense fallback={<PageLoader />}><ExpertiseManagementPage /></Suspense>} />
                  <Route path="activity" element={<Suspense fallback={<PageLoader />}><ActivityLogPage /></Suspense>} />
                  <Route path="calendar" element={<Suspense fallback={<PageLoader />}><GoogleCalendarStatusPage /></Suspense>} />
                  <Route path="calendar-settings" element={<Suspense fallback={<PageLoader />}><CalendarSettingsPage /></Suspense>} />
                  <Route path="appearance" element={<Suspense fallback={<PageLoader />}><AppearanceAdminPage /></Suspense>} />
                  <Route path="plugins" element={<Suspense fallback={<PageLoader />}><PluginsManagementPage /></Suspense>} />
                  <Route path="statistics" element={<Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>} />
                  <Route path="recurring" element={<Suspense fallback={<PageLoader />}><RecurringRulesSettingsPage /></Suspense>} />
                  <Route path="operating-hours" element={<Suspense fallback={<PageLoader />}><OperatingHoursPage /></Suspense>} />
                  <Route path="booking-limits" element={<Suspense fallback={<PageLoader />}><BookingLimitsPage /></Suspense>} />
                </Route>

                {/* Catch all - redirect to my-bookings */}
                <Route path="*" element={<Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />} />
              </Routes>
            </ArcadeTransitionProvider>
            </BrowserRouter>

            {/* Toast Notifications - Using custom toast component with X button */}
            <Toaster
              position="top-center"
              containerStyle={{
                top: '80px', // Below the header to not hide user menu
              }}
              toastOptions={{
                duration: 4000,
              }}
            />

            {/* AImry Support Widget - Now integrated in navbar via AImryNavButton */}

            {/* React Query Devtools — disabled */}
          </QueryClientProvider>
          </AImryBubbleProvider>
        </PluginProvider>
      </LayoutProvider>
    </ThemeProvider>
    </SystemSettingsProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
